import {
  BMComponentsType,
  BMPrimitives,
  useBMTheme,
} from "@traveloka/district-bloom";
import { ReactNode, useEffect, useId, useRef, useState } from "react";
import {
  Animated,
  Dimensions,
  Platform,
  StyleProp,
  StyleSheet,
  ViewStyle,
} from "react-native";
import { useScrollLock } from "../hooks";
import { ModalManager } from "../Modal";
import Portal from "../Portal";
import BMView from "../View/BMView";

export type FullScreenDialogProps = {
  visible: boolean;
  children: ReactNode;
  zIndex?: number;
  style?: StyleProp<ViewStyle>;
  durationIn?: number;
  durationOut?: number;
  noAnimation?: boolean;
};

const DEFAULT_SCREEN_HEIGHT = Dimensions.get("window").height;

export default function FullScreenDialog({
  visible,
  children,
  zIndex = 9,
  style,
  durationIn = BMPrimitives.BMAnimation.duration.quick,
  durationOut = BMPrimitives.BMAnimation.duration.quick,
  noAnimation = false,
}: FullScreenDialogProps) {
  const { BMComponents } = useBMTheme();
  const styles = createStyles(BMComponents);

  const dialogID = useId();
  const translateY = useRef(new Animated.Value(0)).current;

  const [mount, setMount] = useState(false);
  const [screenHeight, setScreenHeight] = useState(DEFAULT_SCREEN_HEIGHT);

  useScrollLock(!ModalManager.empty);

  // Handle screen height changes
  useEffect(() => {
    if (Platform.OS === "web") return;
    const onChange = ({ window }: { window: { height: number } }) => {
      setScreenHeight(window.height);
    };
    const subscription = Dimensions.addEventListener("change", onChange);
    return () => subscription?.remove?.();
  }, []);

  // Modal registration
  useEffect(() => {
    return () => ModalManager.remove(dialogID);
  }, [dialogID]);

  // Show/Hide Animation
  useEffect(() => {
    if (!visible) return handleExit();
    handleEnter();
  }, [visible, screenHeight]);

  const handleEnter = () => {
    setMount(true);
    ModalManager.add(dialogID, setMount);

    if (noAnimation) {
      translateY.setValue(0);
    } else {
      translateY.setValue(screenHeight);
      Animated.timing(translateY, {
        toValue: 0,
        duration: durationIn,
        useNativeDriver: Platform.OS === "web" ? false : true,
      }).start();
    }
  };

  const handleExit = () => {
    if (noAnimation) {
      translateY.setValue(screenHeight);

      setMount(false);
      ModalManager.remove(dialogID);
    } else {
      Animated.timing(translateY, {
        toValue: screenHeight,
        duration: durationOut,
        useNativeDriver: Platform.OS === "web" ? false : true,
      }).start(() => {
        setMount(false);
        ModalManager.remove(dialogID);
      });
    }
  };

  if (!mount) return null;

  return (
    <Portal>
      <BMView
        style={[
          styles.wrapper,
          { zIndex },
          Platform.OS === "web" ? styles.webWrapper : {},
        ]}
      >
        <Animated.View
          style={[
            styles.container,
            {
              transform: [{ translateY }],
            },
            style,
          ]}
        >
          <BMView style={styles.content}>{children}</BMView>
        </Animated.View>
      </BMView>
    </Portal>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    wrapper: {
      flex: 1,
      ...StyleSheet.absoluteFillObject,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
    },
    container: {
      flex: 1,
      justifyContent: "flex-end",
    },
    content: {
      flex: 1,
    },
    webWrapper: {
      // @ts-expect-error position fixed is not available in the react-native typing (this styling is only on web)
      position: "fixed",
    },
  });
}
